import React, { useState } from 'react';
import { PackageMap } from './opportunities/PackageMap';
import { PackageListPanel } from './opportunities/PackageListPanel';
import { PackageCarousel } from './opportunities/PackageCarousel';
import { EmergencyDetailModal } from './opportunities/EmergencyDetailModal';
import { EmergencyIntervention } from './opportunities/EmergencyHoverTooltip';
import { X, Save, Plus, Minus, Clock, Edit2 } from 'lucide-react';
// Types
interface Property {
  id: number;
  address: string;
  mq: number;
  rooms: number;
  bathrooms: number;
  timeWindow: string;
  tags: string[];
  lat: number;
  lng: number;
  isAvailable?: boolean;
}
interface PackageData {
  id: number;
  name: string;
  zoneName: string;
  lat: number;
  lng: number;
  count: number;
  properties: Property[];
  baseEarnings: number;
  status: 'available' | 'accepted' | 'assigned' | 'completed';
  completedDate?: string;
  groupingReason?: string;
  groupingTags?: string[];
}
interface OpportunitiesPageProps {
  onNavigateToDetail: (id: number) => void;
}
export function OpportunitiesPage({
  onNavigateToDetail
}: OpportunitiesPageProps) {
  // --- State ---
  const [activePackageId, setActivePackageId] = useState<number>(1);
  const [statusFilter, setStatusFilter] = useState<'available' | 'accepted' | 'assigned' | 'completed'>('available');
  // Edit Mode State
  const [isEditing, setIsEditing] = useState(false);
  const [editedProperties, setEditedProperties] = useState<Property[]>([]);
  const [nearbyProperties, setNearbyProperties] = useState<Property[]>([]);
  const [editPackageName, setEditPackageName] = useState('');
  // Panel collapse state
  const [isPanelCollapsed, setIsPanelCollapsed] = useState(false);
  // Emergency Modal State
  const [selectedEmergencyId, setSelectedEmergencyId] = useState<number | null>(null);
  const [emergencyPosition, setEmergencyPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  // --- Mock Data ---
  const allProperties: Property[] = [
  // Prati Zone
  {
    id: 101,
    address: 'Via Cola di Rienzo 28',
    mq: 85,
    rooms: 3,
    bathrooms: 2,
    timeWindow: '10:00 - 14:00',
    tags: ['Parquet', 'No animali'],
    lat: 41.9085,
    lng: 12.4665,
    isAvailable: true
  }, {
    id: 102,
    address: 'Via Crescenzio 12',
    mq: 60,
    rooms: 2,
    bathrooms: 1,
    timeWindow: '11:00 - 15:00',
    tags: ['Ascensore'],
    lat: 41.906,
    lng: 12.464,
    isAvailable: true
  }, {
    id: 103,
    address: 'Piazza Cavour 5',
    mq: 110,
    rooms: 4,
    bathrooms: 2,
    timeWindow: '09:00 - 13:00',
    tags: ['Lusso'],
    lat: 41.9065,
    lng: 12.469,
    isAvailable: true
  }, {
    id: 104,
    address: 'Via Orazio 4',
    mq: 70,
    rooms: 2,
    bathrooms: 1,
    timeWindow: '12:00 - 16:00',
    tags: ['Balcone'],
    lat: 41.909,
    lng: 12.463,
    isAvailable: true
  }, {
    id: 105,
    address: 'Via Tacito 18',
    mq: 65,
    rooms: 2,
    bathrooms: 1,
    timeWindow: '09:00 - 12:00',
    tags: ['Ascensore', 'Cortile'],
    lat: 41.905,
    lng: 12.465,
    isAvailable: true
  }, {
    id: 106,
    address: 'Via Ottaviano 22',
    mq: 90,
    rooms: 3,
    bathrooms: 2,
    timeWindow: '13:00 - 17:00',
    tags: ['Terrazzo'],
    lat: 41.908,
    lng: 12.461,
    isAvailable: true
  },
  // Centro Zone
  {
    id: 201,
    address: 'Via del Corso 150',
    mq: 90,
    rooms: 3,
    bathrooms: 2,
    timeWindow: '10:30 - 14:30',
    tags: ['ZTL'],
    lat: 41.903,
    lng: 12.479,
    isAvailable: true
  }, {
    id: 202,
    address: 'Piazza di Spagna 3',
    mq: 120,
    rooms: 4,
    bathrooms: 3,
    timeWindow: '11:00 - 15:00',
    tags: ['Lusso', 'Fragile'],
    lat: 41.9058,
    lng: 12.4823,
    isAvailable: true
  }, {
    id: 203,
    address: 'Via del Tritone 20',
    mq: 75,
    rooms: 2,
    bathrooms: 1,
    timeWindow: '09:30 - 13:00',
    tags: ['ZTL', 'Ascensore'],
    lat: 41.902,
    lng: 12.484,
    isAvailable: true
  }, {
    id: 204,
    address: 'Via Condotti 5',
    mq: 100,
    rooms: 3,
    bathrooms: 2,
    timeWindow: '10:00 - 14:00',
    tags: ['Lusso', 'Portiere'],
    lat: 41.906,
    lng: 12.481,
    isAvailable: true
  }, {
    id: 205,
    address: 'Via del Gambero 3',
    mq: 55,
    rooms: 2,
    bathrooms: 1,
    timeWindow: '14:00 - 17:00',
    tags: ['Scale'],
    lat: 41.903,
    lng: 12.478,
    isAvailable: true
  },
  // Trastevere Zone
  {
    id: 301,
    address: 'Via della Lungaretta 12',
    mq: 55,
    rooms: 2,
    bathrooms: 1,
    timeWindow: '10:00 - 13:00',
    tags: ['Scale'],
    lat: 41.8895,
    lng: 12.4715,
    isAvailable: true
  }, {
    id: 302,
    address: 'Piazza Santa Maria 4',
    mq: 75,
    rooms: 3,
    bathrooms: 1,
    timeWindow: '11:00 - 14:00',
    tags: ['Rumoroso'],
    lat: 41.889,
    lng: 12.47,
    isAvailable: true
  },
  // Testaccio Zone
  {
    id: 401,
    address: 'Via Marmorata 32',
    mq: 65,
    rooms: 2,
    bathrooms: 1,
    timeWindow: '09:30 - 12:30',
    tags: ['Scale', 'Cortile interno'],
    lat: 41.8768,
    lng: 12.4748,
    isAvailable: true
  }, {
    id: 402,
    address: 'Via Galvani 15',
    mq: 80,
    rooms: 3,
    bathrooms: 2,
    timeWindow: '10:00 - 14:00',
    tags: ['Ascensore'],
    lat: 41.8755,
    lng: 12.4762,
    isAvailable: true
  }, {
    id: 403,
    address: 'Piazza Testaccio 8',
    mq: 55,
    rooms: 2,
    bathrooms: 1,
    timeWindow: '11:00 - 13:30',
    tags: ['Parquet'],
    lat: 41.8772,
    lng: 12.473,
    isAvailable: true
  }, {
    id: 404,
    address: 'Via Zabaglia 10',
    mq: 70,
    rooms: 2,
    bathrooms: 1,
    timeWindow: '09:00 - 12:00',
    tags: ['Balcone', 'Ascensore'],
    lat: 41.878,
    lng: 12.476,
    isAvailable: true
  }, {
    id: 405,
    address: 'Via Luca della Robbia 8',
    mq: 60,
    rooms: 2,
    bathrooms: 1,
    timeWindow: '13:00 - 16:00',
    tags: ['Parquet'],
    lat: 41.877,
    lng: 12.472,
    isAvailable: true
  }, {
    id: 406,
    address: 'Via Mastro Giorgio 5',
    mq: 85,
    rooms: 3,
    bathrooms: 2,
    timeWindow: '10:30 - 14:30',
    tags: ['Cortile interno'],
    lat: 41.875,
    lng: 12.477,
    isAvailable: true
  },
  // San Giovanni Zone
  {
    id: 501,
    address: 'Via Appia Nuova 96',
    mq: 95,
    rooms: 3,
    bathrooms: 2,
    timeWindow: '10:00 - 14:00',
    tags: ['Ascensore', 'Condominio'],
    lat: 41.886,
    lng: 12.5065,
    isAvailable: true
  }, {
    id: 502,
    address: 'Via Merulana 22',
    mq: 85,
    rooms: 3,
    bathrooms: 2,
    timeWindow: '11:00 - 15:00',
    tags: ['Balcone'],
    lat: 41.8875,
    lng: 12.504,
    isAvailable: true
  }, {
    id: 503,
    address: 'Via Emanuele Filiberto 30',
    mq: 70,
    rooms: 2,
    bathrooms: 1,
    timeWindow: '09:00 - 12:30',
    tags: ['Scale', 'Luminoso'],
    lat: 41.889,
    lng: 12.507,
    isAvailable: true
  }, {
    id: 504,
    address: 'Via Conte Verde 15',
    mq: 80,
    rooms: 3,
    bathrooms: 1,
    timeWindow: '14:00 - 17:30',
    tags: ['Ascensore'],
    lat: 41.888,
    lng: 12.503,
    isAvailable: true
  }, {
    id: 505,
    address: 'Piazza San Giovanni 1',
    mq: 105,
    rooms: 4,
    bathrooms: 2,
    timeWindow: '10:00 - 14:00',
    tags: ['Lusso', 'Portiere'],
    lat: 41.886,
    lng: 12.508,
    isAvailable: true
  }];
  const [packages, setPackages] = useState<PackageData[]>([{
    id: 1,
    name: 'Pacchetto Zona Prati',
    zoneName: 'Prati',
    lat: 41.907,
    lng: 12.4665,
    count: 3,
    baseEarnings: 140,
    status: 'available',
    properties: [allProperties[0], allProperties[1], allProperties[2]],
    groupingReason: 'Proprietà entro 500m, finestre orarie sovrapposte (09-15h). Percorso ottimale: 12 min a piedi.',
    groupingTags: ['Prossimità', 'Orari compatibili', 'Stesso quartiere']
  }, {
    id: 2,
    name: 'Pacchetto Centro Storico',
    zoneName: 'Centro',
    lat: 41.904,
    lng: 12.48,
    count: 2,
    baseEarnings: 110,
    status: 'available',
    properties: [allProperties[6], allProperties[7]],
    groupingReason: 'Entrambe in ZTL, stesso accesso veicolare. Proprietà lusso con requisiti simili.',
    groupingTags: ['ZTL condivisa', 'Segmento lusso', 'Stessa via']
  }, {
    id: 3,
    name: 'Pacchetto Trastevere',
    zoneName: 'Trastevere',
    lat: 41.8892,
    lng: 12.4708,
    count: 2,
    baseEarnings: 95,
    status: 'accepted',
    properties: [allProperties[11], allProperties[12]],
    groupingReason: 'Proprietà adiacenti in zona pedonale. Nessun parcheggio necessario.',
    groupingTags: ['Zona pedonale', 'Adiacenti', 'Facile accesso']
  }, {
    id: 4,
    name: 'Pacchetto Testaccio',
    zoneName: 'Testaccio',
    lat: 41.8765,
    lng: 12.4747,
    count: 3,
    baseEarnings: 100,
    status: 'available',
    properties: [allProperties[13], allProperties[14], allProperties[15]],
    groupingReason: 'Cluster residenziale con ascensore. Finestre orarie consecutive, percorso lineare.',
    groupingTags: ['Con ascensore', 'Orari consecutivi', 'Percorso lineare']
  }, {
    id: 5,
    name: 'Pacchetto San Giovanni',
    zoneName: 'San Giovanni',
    lat: 41.8868,
    lng: 12.5053,
    count: 2,
    baseEarnings: 120,
    status: 'available',
    properties: [allProperties[19], allProperties[20]],
    groupingReason: 'Proprietà sulla stessa via, metratura simile. Ottimo rapporto guadagno/tempo.',
    groupingTags: ['Stessa via', 'Metratura simile', 'Alto rendimento']
  }, {
    id: 6,
    name: 'Pacchetto Parioli',
    zoneName: 'Parioli',
    lat: 41.923,
    lng: 12.492,
    count: 3,
    baseEarnings: 155,
    status: 'assigned',
    properties: [{
      id: 601,
      address: 'Viale Parioli 45',
      mq: 110,
      rooms: 4,
      bathrooms: 2,
      timeWindow: '09:00 - 13:00',
      tags: ['Lusso', 'Portiere'],
      lat: 41.9235,
      lng: 12.4925
    }, {
      id: 602,
      address: 'Via Panama 12',
      mq: 90,
      rooms: 3,
      bathrooms: 2,
      timeWindow: '10:00 - 14:00',
      tags: ['Parquet'],
      lat: 41.922,
      lng: 12.491
    }, {
      id: 603,
      address: 'Piazza Ungheria 3',
      mq: 75,
      rooms: 2,
      bathrooms: 1,
      timeWindow: '11:00 - 15:00',
      tags: ['Ascensore'],
      lat: 41.9225,
      lng: 12.493
    }],
    groupingReason: 'Quartiere residenziale premium. Portiere facilita accesso, parcheggio disponibile.',
    groupingTags: ['Premium', 'Portiere', 'Parcheggio facile']
  }, {
    id: 7,
    name: 'Pacchetto EUR',
    zoneName: 'EUR',
    lat: 41.835,
    lng: 12.47,
    count: 2,
    baseEarnings: 130,
    status: 'completed',
    completedDate: '05/02/2026',
    properties: [{
      id: 701,
      address: 'Viale Europa 55',
      mq: 100,
      rooms: 3,
      bathrooms: 2,
      timeWindow: '09:00 - 13:00',
      tags: ['Ufficio', 'Lusso'],
      lat: 41.8355,
      lng: 12.4705
    }, {
      id: 702,
      address: 'Viale America 20',
      mq: 85,
      rooms: 3,
      bathrooms: 2,
      timeWindow: '14:00 - 18:00',
      tags: ['Parcheggio'],
      lat: 41.8345,
      lng: 12.4695
    }],
    groupingReason: 'Stesso complesso direzionale, orari mattina/pomeriggio complementari.',
    groupingTags: ['Stesso complesso', 'Orari complementari']
  }]);
  // Mock Emergency Interventions
  const emergencyInterventions: EmergencyIntervention[] = [{
    id: 901,
    lat: 41.895,
    lng: 12.485,
    address: 'Via dei Fori Imperiali 10',
    zoneName: 'Centro',
    cleaningType: 'Pulizia Emergenza Post-Allagamento',
    description: 'Allagamento bagno principale, acqua su pavimento parquet. Necessaria asciugatura e pulizia profonda.',
    mq: 95,
    rooms: 3,
    bathrooms: 2,
    floor: 3,
    tags: ['Parquet', 'Fragile', 'Urgente'],
    hostName: 'Giovanni M.',
    nextCheckIn: '16:00',
    deadlineMs: Date.now() + 2 * 60 * 60 * 1000,
    quotesReceived: 3,
    suggestedPriceMin: 85,
    suggestedPriceMax: 120
  }, {
    id: 902,
    lat: 41.912,
    lng: 12.455,
    address: 'Viale Vaticano 45',
    zoneName: 'Vaticano',
    cleaningType: 'Pulizia Profonda Post-Festa',
    description: 'Festa in appartamento, necessaria pulizia profonda di tutte le stanze e sanificazione.',
    mq: 110,
    rooms: 4,
    bathrooms: 2,
    floor: 2,
    tags: ['Sanificazione', 'Rifiuti extra'],
    hostName: 'Laura B.',
    nextCheckIn: '11:00 domani',
    deadlineMs: Date.now() + 5 * 60 * 60 * 1000,
    quotesReceived: 1,
    suggestedPriceMin: 95,
    suggestedPriceMax: 145
  }, {
    id: 903,
    lat: 41.8835,
    lng: 12.4895,
    address: 'Via Cavour 78',
    zoneName: 'Monti',
    cleaningType: 'Emergenza Rottura Tubo',
    description: 'Rottura tubo sotto il lavandino della cucina. Acqua su tutto il pavimento, necessaria asciugatura urgente e pulizia.',
    mq: 65,
    rooms: 2,
    bathrooms: 1,
    floor: 1,
    tags: ['Urgente', 'Danni acqua', 'Piano terra'],
    hostName: 'Marco R.',
    nextCheckIn: '14:00',
    deadlineMs: Date.now() + 1.5 * 60 * 60 * 1000,
    quotesReceived: 5,
    suggestedPriceMin: 70,
    suggestedPriceMax: 100
  }, {
    id: 904,
    lat: 41.8725,
    lng: 12.4802,
    address: 'Viale Aventino 32',
    zoneName: 'Aventino',
    cleaningType: 'Pulizia Urgente Check-out Anticipato',
    description: 'Ospite partito in anticipo, appartamento in condizioni pessime. Prossimo check-in tra poche ore.',
    mq: 80,
    rooms: 3,
    bathrooms: 1,
    floor: 4,
    tags: ['Check-out', 'Urgente', 'Ascensore'],
    hostName: 'Francesca L.',
    nextCheckIn: '15:00',
    deadlineMs: Date.now() + 3 * 60 * 60 * 1000,
    quotesReceived: 2,
    suggestedPriceMin: 75,
    suggestedPriceMax: 110
  }, {
    id: 905,
    lat: 41.9155,
    lng: 12.4725,
    address: 'Via Cola di Rienzo 185',
    zoneName: 'Prati',
    cleaningType: 'Sanificazione Post-Infestazione',
    description: 'Segnalata infestazione insetti. Necessaria sanificazione completa e pulizia profonda di tutti gli ambienti.',
    mq: 100,
    rooms: 4,
    bathrooms: 2,
    floor: 5,
    tags: ['Sanificazione', 'Urgente', 'Prodotti speciali'],
    hostName: 'Alessandro V.',
    nextCheckIn: '12:00 domani',
    deadlineMs: Date.now() + 4 * 60 * 60 * 1000,
    quotesReceived: 0,
    suggestedPriceMin: 110,
    suggestedPriceMax: 160
  }];
  // Filter Logic — show only available and assigned packages
  const filteredPackages = packages.filter(p => {
    return p.status === 'available' || p.status === 'assigned';
  });
  // --- Handlers ---
  const handleEditStart = () => {
    const pkg = packages.find(p => p.id === activePackageId);
    if (!pkg) return;
    setEditPackageName(pkg.name);
    setEditedProperties([...pkg.properties]);
    // Find nearby properties not in current package
    const currentIds = new Set(pkg.properties.map(p => p.id));
    const nearby = allProperties.filter(p => !currentIds.has(p.id) && Math.abs(p.lat - pkg.lat) < 0.01 && Math.abs(p.lng - pkg.lng) < 0.01);
    setNearbyProperties(nearby);
    setIsEditing(true);
  };
  const handleToggleProperty = (property: Property) => {
    const isIncluded = editedProperties.some(p => p.id === property.id);
    if (isIncluded) {
      // Remove: move from edited to nearby
      setEditedProperties(prev => prev.filter(p => p.id !== property.id));
      setNearbyProperties(prev => [...prev, property]);
    } else {
      // Add: move from nearby to edited
      setNearbyProperties(prev => prev.filter(p => p.id !== property.id));
      setEditedProperties(prev => [...prev, property]);
    }
  };
  const handleEditSave = () => {
    setPackages(prev => prev.map(p => {
      if (p.id === activePackageId) {
        return {
          ...p,
          name: editPackageName,
          properties: editedProperties,
          count: editedProperties.length
        };
      }
      return p;
    }));
    setIsEditing(false);
  };
  const handleEditCancel = () => {
    setIsEditing(false);
    setEditedProperties([]);
    setNearbyProperties([]);
  };
  const handleAccept = (id: number) => {
    setPackages(prev => prev.map(p => p.id === id ? {
      ...p,
      status: p.status === 'available' ? 'accepted' : 'assigned'
    } : p));
    if (statusFilter === 'available') {
      alert(`Pacchetto #${id} accettato! Ora puoi assegnare il team.`);
    } else if (statusFilter === 'accepted') {
      alert(`Team assegnato al pacchetto #${id}!`);
    }
  };
  // Emergency Handlers
  const handleEmergencyClick = (id: number) => {
    setSelectedEmergencyId(id);
    // Note: position will be updated by the map component via onEmergencyPositionUpdate
  };
  const handleMapClick = () => {
    if (selectedEmergencyId) {
      setSelectedEmergencyId(null);
      setEmergencyPosition(null);
    }
  };
  const handleQuoteSubmit = (quote: {
    operatorId: number;
    position: string;
    price: number;
  }) => {
    console.log('Quote submitted:', quote);
    alert(`Preventivo inviato con successo! Offerta: €${quote.price}`);
    setSelectedEmergencyId(null);
    setEmergencyPosition(null);
  };
  const selectedEmergency = emergencyInterventions.find(e => e.id === selectedEmergencyId);
  return <div className="relative h-screen overflow-hidden" data-id="element-1609">
      {/* Full-width Map */}
      <div className="absolute inset-0" style={{
      paddingTop: '0px',
      paddingBottom: '0px'
    }} onClick={e => {
      // Only close if clicking the map container directly (background)
      // Leaflet events are handled inside PackageMap, but this catches clicks on the container div
      if (e.target === e.currentTarget) {
        handleMapClick();
      }
    }} data-id="element-1610">
        <PackageMap packages={filteredPackages} activePackageId={activePackageId} onPackageSelect={setActivePackageId} isEditing={isEditing} editedProperties={editedProperties} nearbyProperties={nearbyProperties} onToggleProperty={handleToggleProperty} emergencyInterventions={emergencyInterventions} onEmergencyClick={handleEmergencyClick} selectedEmergencyId={selectedEmergencyId} onEmergencyPositionUpdate={setEmergencyPosition} data-id="element-1611" />
      </div>

      {/* Floating List Panel */}
      <PackageListPanel packages={filteredPackages} activePackageId={activePackageId} onSelect={setActivePackageId} onEdit={handleEditStart} onAccept={handleAccept} isCollapsed={isPanelCollapsed} onToggleCollapse={() => setIsPanelCollapsed(!isPanelCollapsed)} data-id="element-1612" />

      {/* Edit Mode Floating Panel (Overlay on Map) */}
      {isEditing && <div className="absolute top-4 right-4 z-[1000] bg-white rounded-xl shadow-2xl border border-gray-200 w-80 overflow-hidden flex flex-col max-h-[calc(100%-32px)]" data-id="element-1613">
          <div className="p-4 border-b border-gray-200 bg-teal-deep text-white" data-id="element-1614">
            <h3 className="font-bold text-sm flex items-center gap-2" data-id="element-1615">
              <Edit2 size={16} data-id="element-1616" />
              Modifica Pacchetto
            </h3>
          </div>

          <div className="p-4 border-b border-gray-100" data-id="element-1617">
            <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-1618">
              Nome Pacchetto
            </label>
            <input type="text" value={editPackageName} onChange={e => setEditPackageName(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-deep outline-none" data-id="element-1619" />
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-4" data-id="element-1620">
            <div data-id="element-1621">
              <h4 className="text-xs font-bold text-navy uppercase tracking-wider mb-2" data-id="element-1622">
                Inclusi ({editedProperties.length})
              </h4>
              <div className="space-y-2" data-id="element-1623">
                {editedProperties.map(prop => <div key={prop.id} className="flex items-center justify-between bg-gray-50 p-2 rounded border border-gray-200" data-id="element-1624">
                    <div className="truncate text-xs font-medium text-navy flex-1 mr-2" data-id="element-1625">
                      {prop.address}
                    </div>
                    <button onClick={() => handleToggleProperty(prop)} className="text-red-500 hover:bg-red-50 p-1 rounded" data-id="element-1626">
                      <Minus size={14} data-id="element-1627" />
                    </button>
                  </div>)}
              </div>
            </div>

            {nearbyProperties.length > 0 && <div data-id="element-1628">
                <h4 className="text-xs font-bold text-navy uppercase tracking-wider mb-2" data-id="element-1629">
                  Disponibili Vicini ({nearbyProperties.length})
                </h4>
                <div className="space-y-2" data-id="element-1630">
                  {nearbyProperties.map(prop => <div key={prop.id} className="flex items-center justify-between bg-white p-2 rounded border border-dashed border-gray-300 hover:border-teal-deep transition-colors" data-id="element-1631">
                      <div className="truncate text-xs text-gray-600 flex-1 mr-2" data-id="element-1632">
                        {prop.address}
                      </div>
                      <button onClick={() => handleToggleProperty(prop)} className="text-green-600 hover:bg-green-50 p-1 rounded" data-id="element-1633">
                        <Plus size={14} data-id="element-1634" />
                      </button>
                    </div>)}
                </div>
              </div>}
          </div>

          <div className="p-4 border-t border-gray-200 bg-gray-50 flex gap-2" data-id="element-1635">
            <button onClick={handleEditCancel} className="flex-1 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-600 hover:bg-white transition-colors" data-id="element-1636">
              Annulla
            </button>
            <button onClick={handleEditSave} className="flex-1 py-2 bg-teal-deep text-white rounded-lg text-sm font-medium hover:bg-teal-deep/90 transition-colors flex items-center justify-center gap-2" data-id="element-1637">
              <Save size={16} data-id="element-1638" />
              Salva
            </button>
          </div>
        </div>}

      {/* Emergency Detail Modal (Floating Panel) */}
      {selectedEmergency && <EmergencyDetailModal intervention={selectedEmergency} isOpen={!!selectedEmergency} onClose={() => {
      setSelectedEmergencyId(null);
      setEmergencyPosition(null);
    }} onSubmit={handleQuoteSubmit} position={emergencyPosition} data-id="element-1639" />}
    </div>;
}